import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Send, 
  Loader2, 
  AlertCircle,
  Copy,
  CheckCircle,
  Lightbulb,
  SlidersHorizontal,
  Layout,
  ArrowRight,
  Layers,
  ListChecks,
  FileText
} from 'lucide-react';
import { streamOpenAIResponse } from '../../lib/openai';

interface CarouselSlide {
  title: string;
  bullet_points: string[];
}

const CarouselOutlineBuilder = () => {
  const [topic, setTopic] = useState('');
  const [slideCount, setSlideCount] = useState<number>(5);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slides, setSlides] = useState<CarouselSlide[]>([]);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!topic.trim()) {
      setError('Please enter a topic for your carousel');
      return;
    }

    if (slideCount < 3 || slideCount > 10) {
      setError('Please select between 3 and 10 slides');
      return;
    }

    setLoading(true);
    setError(null);
    setSlides([]);

    try {
      const prompt = `You are a carousel content strategist; Topic=${topic}; Slides=${slideCount} (3-10); output ONLY JSON array "slides" with each object containing "title" (≤7 words) and "bullet_points" (2-4 bullets, each ≤14 words); no additional text.`;

      let responseText = '';

      await streamOpenAIResponse(
        [{ role: 'user', content: prompt }],
        (chunk) => {
          responseText += chunk;
          try {
            // Clean the response text to handle markdown code blocks
            let cleanedResponse = responseText;
            if (cleanedResponse.includes('```json')) {
              cleanedResponse = cleanedResponse.replace(/```json\n|\n```/g, '');
            } else if (cleanedResponse.includes('```')) {
              cleanedResponse = cleanedResponse.replace(/```\n|\n```/g, '');
            }
            
            // Try to parse as a complete JSON object
            const parsedData = JSON.parse(cleanedResponse);
            if (Array.isArray(parsedData.slides)) {
              setSlides(parsedData.slides);
            } else if (Array.isArray(parsedData)) {
              setSlides(parsedData);
            }
          } catch (e) {
            // Ignore parsing errors during streaming
          }
        },
        (error) => {
          throw error;
        }
      );
    } catch (err) {
      setError('Failed to generate carousel outline. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (index: number) => {
    const slide = slides[index];
    if (!slide) return;
    
    const text = `Slide ${index + 1}: ${slide.title}\n\n${slide.bullet_points.map(point => `• ${point}`).join('\n')}`;
    
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    
    setTimeout(() => {
      setCopiedIndex(null);
    }, 2000);
  };

  const handleCopyAll = () => {
    if (slides.length === 0) return;
    
    const text = slides.map((slide, index) => 
      `Slide ${index + 1}: ${slide.title}\n${slide.bullet_points.map(point => `• ${point}`).join('\n')}`
    ).join('\n\n');
    
    navigator.clipboard.writeText(text);
    setCopiedIndex(-1); // -1 indicates "copy all"
    
    setTimeout(() => {
      setCopiedIndex(null);
    }, 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="flex items-center space-x-4">
        <Link to="/ai-tools" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Carousel Outline Builder</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Create structured outlines for engaging carousel posts on Instagram and LinkedIn
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Generate Carousel Outline</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Topic Input */}
          <div>
            <label htmlFor="topic" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Carousel Topic
            </label>
            <input
              type="text"
              id="topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g., Productivity Tips, Social Media Strategy, Personal Finance"
              className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
            />
          </div>

          {/* Slide Count */}
          <div>
            <label htmlFor="slideCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Number of Slides
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                id="slideCount"
                min="3"
                max="10"
                value={slideCount}
                onChange={(e) => setSlideCount(parseInt(e.target.value))}
                className="flex-1 h-2 bg-gray-200 dark:bg-dark-700 rounded-lg appearance-none cursor-pointer"
              />
              <span className="w-8 text-center text-gray-700 dark:text-gray-300">{slideCount}</span>
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Select between 3-10 slides for your carousel
            </p>
          </div>

          <div className="flex justify-end">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading || !topic.trim()}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                loading || !topic.trim()
                  ? 'bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Layout className="h-5 w-5" />
                  <span>Generate Outline</span>
                </>
              )}
            </motion.button>
          </div>
        </form>
      </div>

      {/* Results */}
      {slides.length > 0 && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <Layers className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
              Carousel Outline ({slides.length} slides)
            </h2>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleCopyAll}
              className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
            >
              {copiedIndex === -1 ? (
                <>
                  <CheckCircle className="h-4 w-4" />
                  <span>Copied All</span>
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  <span>Copy All</span>
                </>
              )}
            </motion.button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {slides.map((slide, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-dark-800 rounded-xl p-6 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-full font-semibold text-sm">
                      {index + 1}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {slide.title}
                    </h3>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleCopy(index)}
                    className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"
                  >
                    {copiedIndex === index ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <Copy className="h-5 w-5" />
                    )}
                  </motion.button>
                </div>

                <div className="space-y-3 mt-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
                    <ListChecks className="h-4 w-4 mr-1.5 text-indigo-600 dark:text-indigo-400" />
                    Bullet Points
                  </h4>
                  <ul className="space-y-2">
                    {slide.bullet_points.map((point, pointIndex) => (
                      <li key={pointIndex} className="flex items-start">
                        <span className="text-indigo-600 dark:text-indigo-400 mr-2">•</span>
                        <p className="text-gray-700 dark:text-gray-300">{point}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Tips Section */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center space-x-3 mb-4">
          <Lightbulb className="h-5 w-5" />
          <h3 className="text-lg font-semibold">Carousel Best Practices</h3>
        </div>
        <ul className="space-y-3 text-indigo-100">
          <li className="flex items-start space-x-2">
            <ArrowRight className="h-4 w-4 mt-1" />
            <span>Start with a strong hook in your first slide to capture attention</span>
          </li>
          <li className="flex items-start space-x-2">
            <ArrowRight className="h-4 w-4 mt-1" />
            <span>Keep titles concise and compelling - under 7 words is ideal</span>
          </li>
          <li className="flex items-start space-x-2">
            <ArrowRight className="h-4 w-4 mt-1" />
            <span>Use bullet points to make information scannable and digestible</span>
          </li>
          <li className="flex items-start space-x-2">
            <ArrowRight className="h-4 w-4 mt-1" />
            <span>End with a clear call-to-action in your final slide</span>
          </li>
        </ul>
      </div>

      {/* Additional Tips */}
      <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-6 border border-amber-100 dark:border-amber-800/30">
        <div className="flex items-center space-x-4 mb-4">
          <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
            <FileText className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-amber-900 dark:text-amber-100">Design Tips</h3>
            <p className="text-amber-800 dark:text-amber-200">Make your carousel visually appealing</p>
          </div>
        </div>
        <ul className="space-y-2 text-amber-700 dark:text-amber-300">
          <li className="flex items-start space-x-2">
            <span className="text-amber-600 dark:text-amber-400 mr-1">•</span>
            <span>Use consistent colors and fonts throughout all slides</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-amber-600 dark:text-amber-400 mr-1">•</span>
            <span>Include slide numbers to help users track their progress</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-amber-600 dark:text-amber-400 mr-1">•</span>
            <span>Use high-quality images or graphics that support your message</span>
          </li>
          <li className="flex items-start space-x-2">
            <span className="text-amber-600 dark:text-amber-400 mr-1">•</span>
            <span>Maintain plenty of white space to avoid overwhelming your audience</span>
          </li>
        </ul>
      </div>
    </motion.div>
  );
};

export default CarouselOutlineBuilder;